import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Pagination, { PaginationPresentationProps, PaginationStyle } from "@insite/mobius/Pagination";
import { SelectPresentationProps } from "@insite/mobius/Select";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { UserDetailsModel } from "@theStellarBlueprint/CustomApiModels";
import CustomApplicationState from "@theStellarBlueprint/Store/CustomApplicationState";
import loadPurchasedItems from "@theStellarBlueprint/Store/Data/PurchasedItems/Handlers/LoadPurchasedItems";
import { getPurchasedItemsData } from "@theStellarBlueprint/Store/Data/PurchasedItems/LoadPurchasedItemsSelector";
import UpdatePurchasedItemsConfigSelection from "@theStellarBlueprint/Store/Data/PurchasedItemsConfig/Handlers/LoadPurchasedItemsConfigHandler";
import { getPurchasedItemsConfigSelectionState } from "@theStellarBlueprint/Store/Data/PurchasedItemsConfig/LoadPurchasedItemsConfigSelector";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showResultsPerPage = "showResultsPerPage",
    showChangePageSelectView = "showChangePageSelectView",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showResultsPerPage]: boolean;
        [fields.showChangePageSelectView]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => ({
    purchasedItemsData: getPurchasedItemsData(state),
    purchasedItemsConfig: getPurchasedItemsConfigSelectionState(state),
});

const mapDispatchToProps = {
    UpdatePurchasedItemsConfigSelection,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface PurchasedItemPaginationStyles {
    pagination?: PaginationPresentationProps;
    perPageSelect?: SelectPresentationProps;
    pageSelect?: SelectPresentationProps;
    pageLinkList?: InjectableCss;
    pageLinkListWrapper?: InjectableCss;
    mobileNavContainer?: InjectableCss;
    paginationStyle?: InjectableCss;
    linkList?: PaginationPresentationProps;
}

export const purchasedItemPaginationStyles: PurchasedItemPaginationStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                flex-wrap: nowrap;
                                > span {
                                    width: 50%;
                                    > div [class^="FormFieldStyle"] {
                                        width: 210px;
                                    }
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
            linkList: css`
                ul li {
                    button {
                        span {
                            color: rgb(0, 0, 0) !important;
                        }
                    }
                    button[aria-current="page"] {
                        cursor: default;
                        border: 1px solid rgb(0, 0, 255) !important;
                        background: #ffffff !important;
                        span {
                            color: rgb(1, 30, 251) !important;
                        }
                    }
                }
            `,
        },
    },
};

const styles = purchasedItemPaginationStyles;

const PurchasedItemsPagination: FC<Props> = ({
    fields,
    purchasedItemsData,
    UpdatePurchasedItemsConfigSelection,
    purchasedItemsConfig,
}) => {
    const UserDetailsData: UserDetailsModel | undefined = purchasedItemsData.value;
    const PurchasedItemPagination: PaginationModel | undefined = UserDetailsData?.pagination;
    const changePage = (newPageIndex: number) => {
        UpdatePurchasedItemsConfigSelection({
            page: newPageIndex.toString(),
            Search: purchasedItemsConfig.Search,
            Description: purchasedItemsConfig.Description,
            fromDate:purchasedItemsConfig.fromDate,
            toDate:purchasedItemsConfig.toDate,
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        UpdatePurchasedItemsConfigSelection({
            pageSize: newPageSize.toString(),
            Search: purchasedItemsConfig.Search,
            Description: purchasedItemsConfig.Description,
            fromDate:purchasedItemsConfig.fromDate,
            toDate:purchasedItemsConfig.toDate,
        });
    };

    if (!PurchasedItemPagination || !PurchasedItemPagination.totalItemCount) {
        return null;
    }

    return (
        <Pagination
            {...styles.pagination}
            resultsCount={PurchasedItemPagination!.totalItemCount}
            currentPage={PurchasedItemPagination!.page}
            resultsPerPage={PurchasedItemPagination!.pageSize}
            resultsPerPageOptions={PurchasedItemPagination!.pageSizeOptions}
            resultsPerPageLabel="show"
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="PurchasedItems-PageSize"
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedItemsPagination),
    definition: {
        group: "Basic",
        displayName: "Purchased Items Pagination",
        fieldDefinitions: [
            {
                name: fields.showResultsPerPage,
                displayName: "Show Results Per Page ",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showChangePageSelectView,
                displayName: "Show Change Page Select View",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
